import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import { assessmentStatusTranslations } from '../constants';
import {
  assessmentColumnHeaders,
  assessmentColumnHeaderAccessors,
  uploadPreparedAssessmentColumnHeaders,
  uploadPreparedAssessmentColumnHeaderAccessors,
} from '../constants';
import { formatDate } from './formatDate';
import { excelExportTypes } from '../constants/excelExportTypes';
import { isChannelLongTail } from './isChannelLongTail';
import { extractSoldToFromJoinedCustomerId } from './extractSoldToFromJoinedCustomerId';
import { uiFriendlyRolesAccessors } from '../constants/uiFriendlyRoles';
import { relevantStatusForYoy } from '../constants/assessmentStatus';

const fileType =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
export const fileExtension = '.xlsx';
export const simpleExportFileName = 'Mcp_Overview';
export const readyForUploadFileName = 'Mcp_Overview_Ready_For_Upload';

const uploadPreparedAssessmentColumnHeadersArray = Object.values(
  uploadPreparedAssessmentColumnHeaders
);
const simpleExportWorkSheetHeadersArray = [
  assessmentColumnHeaders.YEAR,
  assessmentColumnHeaders.C6_ID,
  assessmentColumnHeaders.CUSTOMER_ID,
  assessmentColumnHeaders.CUSTOMER_NAME,
  assessmentColumnHeaders.C6_NAME,
  assessmentColumnHeaders.BUSINESS_UNIT,
  assessmentColumnHeaders.TIER,
  assessmentColumnHeaders.CHANNEL,
  assessmentColumnHeaders.STATUS,
  assessmentColumnHeaders.PENDING_USER,
  assessmentColumnHeaders.SCORE,
  assessmentColumnHeaders.LAST_YEAR_SCORE,
  assessmentColumnHeaders.YEAR_OVER_YEAR,
];

const mapDownloadDataToHeaders = (row, headersArray, headersAccessors) =>
  headersArray.reduce((rowObject, nextHeader) => {
    const accessor = headersAccessors[nextHeader];

    if (nextHeader === assessmentColumnHeaders.STATUS) {
      rowObject[nextHeader] = assessmentStatusTranslations[row[accessor]];
      return rowObject;
    }

    if (nextHeader === assessmentColumnHeaders.PENDING_USER) {
      rowObject[nextHeader] = uiFriendlyRolesAccessors(row[accessor]);
      return rowObject;
    }

    if (nextHeader === assessmentColumnHeaders.YEAR_OVER_YEAR) {
      const yoyChange =
        row.lastYearScore !== null &&
        row.lastYearScore !== undefined &&
        relevantStatusForYoy(row.status)
          ? row.score - row.lastYearScore
          : undefined;
      rowObject[nextHeader] =
        yoyChange !== undefined
          ? `${yoyChange > 0 ? '+' : ''}${yoyChange}%`
          : '';
      return rowObject;
    }

    rowObject[nextHeader] = row[accessor];
    return rowObject;
  }, {});

const getDateAndTimeString = () => {
  let dateString = formatDate(new Date(), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });

  dateString = dateString.replace(',', '');
  return dateString.replace(' ', '_');
};

const exportConfigurations = {
  [excelExportTypes.SIMPLE]: {
    mapper: (assessment) =>
      mapDownloadDataToHeaders(
        assessment,
        simpleExportWorkSheetHeadersArray,
        assessmentColumnHeaderAccessors
      ),
    headers: simpleExportWorkSheetHeadersArray,
  },
  [excelExportTypes.UPLOAD_PREPARED]: {
    mapper: (assessment) =>
      mapDownloadDataToHeaders(
        assessment,
        uploadPreparedAssessmentColumnHeadersArray,
        uploadPreparedAssessmentColumnHeaderAccessors
      ),
    headers: uploadPreparedAssessmentColumnHeadersArray,
  },
};

export const mapAssessmentsByType = (assessments, excelExportType) => {
  const configuration = exportConfigurations[excelExportType];

  if (!configuration) {
    throw new Error(`Unsupported excel export type: ${excelExportType}`);
  }

  const assessmentsToBeExported = assessments.map((assessment) => {
    return configuration.mapper(assessment);
  });

  return {
    assessmentsToBeExported,
    workSheetHeadersArray: configuration.headers,
  };
};

const prepareAssessmentsForDownload = (assessments) => {
  return assessments.map((assessment) => ({
    ...assessment,
    customerId: isChannelLongTail(assessment.channel)
      ? assessment.customerId
      : extractSoldToFromJoinedCustomerId(
          assessment.customerId,
          assessment.c6Id
        ),
  }));
};

const saveAssessmentsToExcelFile = (
  assessmentsForDownload,
  excelExportType
) => {
  assessmentsForDownload = prepareAssessmentsForDownload(
    assessmentsForDownload
  );

  const { assessmentsToBeExported, workSheetHeadersArray } =
    mapAssessmentsByType(assessmentsForDownload, excelExportType);

  const workSheet = XLSX.utils.json_to_sheet(assessmentsToBeExported, {
    header: workSheetHeadersArray,
  });

  const workBook = { Sheets: { data: workSheet }, SheetNames: ['data'] };
  const excelBuffer = XLSX.write(workBook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: fileType });
  const fileName =
    excelExportType === excelExportTypes.UPLOAD_PREPARED
      ? readyForUploadFileName
      : simpleExportFileName;

  FileSaver.saveAs(
    data,
    `${fileName}_${getDateAndTimeString()}${fileExtension}`
  );
};

export { saveAssessmentsToExcelFile };
